import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Instagram, Music2 } from "lucide-react";

const FACTS = [
  { label: "BRAND", value: "Schlafi" },
  { label: "CREATOR", value: "Finn @fionntime" },
  { label: "KATEGORIE", value: "D2C Fashion" },
  { label: "RETAIL", value: "Breuninger" },
];

const STATS = [
  { value: "30.000+", label: "Verkaufte Pyjama-Hosen" },
  { value: "7 %", label: "Retourenquote" },
  { value: "26 %", label: "Wiederkehrende Kunden" },
  { value: "2,9 MIO+", label: "TikTok-Follower @fionntime" },
];

const PRODUCTS = [
  { src: "/schlafi/strawberry.jpg", alt: "Schlafi Strawberry Pyjama-Hose" },
  { src: "/schlafi/banana.jpg", alt: "Schlafi Banana Pyjama-Hose" },
  { src: "/schlafi/juniper.jpg", alt: "Schlafi Juniper Pyjama-Hose" },
  { src: "/schlafi/towel.jpg", alt: "Schlafi Towel Juniper" },
];

export default function Schlafi() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="relative min-h-screen overflow-hidden pb-24 pt-32 md:pt-40">
      <div className="mx-auto max-w-[1200px] px-5 md:px-10">
        <Link to="/" data-testid="schlafi-back-link" className="mono-label inline-flex items-center gap-2 text-xs text-white/60 transition-colors hover:text-[var(--crtr-yellow)]">
          <ArrowLeft className="h-4 w-4" /> ZURÜCK
        </Link>

        <p className="mono-label mt-8 text-xs text-[var(--crtr-yellow)]">CASE 01 — CREATOR BRAND))</p>
        <h1 className="font-display mt-4 text-5xl text-white sm:text-6xl lg:text-7xl">SCHLAFI.</h1>
        <p className="mono-label mt-4 max-w-2xl text-sm text-white/70">
          CREATOR-LED BRAND LAUNCH MIT FIONNTIME. VON DER COMMUNITY ZUR EIGENEN FASHION-BRAND.
        </p>

        {/* campaign hero */}
        <div className="relative mt-12 overflow-hidden rounded-[2rem]" data-testid="schlafi-hero-image">
          <img fetchPriority="high" src="/schlafi/banner.jpg" alt="Schlafi Beach-Kampagne (KI-generiert)" className="w-full object-cover" />
          <span className="ai-badge">KI-generiert</span>
        </div>

        {/* facts */}
        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
          {FACTS.map((f) => (
            <div key={f.label} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <p className="mono-label text-[10px] text-white/50">{f.label}</p>
              <p className="mt-2 font-heavy text-lg uppercase text-white">{f.value}</p>
            </div>
          ))}
        </div>

        {/* stats */}
        <div className="mt-14 grid grid-cols-2 gap-x-6 gap-y-10 border-y border-white/10 py-10 md:grid-cols-4" data-testid="schlafi-stats">
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-display text-3xl text-[var(--crtr-yellow)] md:text-4xl">{s.value}</p>
              <p className="mono-label mt-2 text-[10px] text-white/60">{s.label}</p>
            </div>
          ))}
        </div>

        {/* story */}
        <div className="mt-16 grid gap-10 md:grid-cols-[1.4fr_1fr] md:items-start">
          <div>
            <h2 className="font-display text-3xl text-white md:text-4xl">HOSEN, DIE EINFACH IMMER PASSEN.</h2>
            <div className="mt-6 max-w-xl space-y-4 text-base leading-relaxed text-white/80">
              <p>
                Schlafi steht für Hosen, die einfach immer passen: weich wie eine Schlafhose, lässig wie
                eine Jogginghose und bequem genug für den ganzen Tag. Gegründet von Content Creator
                Finn (@fionntime) — entwickelt mit der Community, gelauncht über Social und binnen
                kürzester Zeit zur eigenständigen D2C-Fashion-Brand gewachsen.
              </p>
              <p>
                Vom klassischen Schlafi über Schorti, Super Schorti und Swim Shorts bis zu Boxer Shorts,
                Bags und Towels: Jeder Drop entsteht aus dem direkten Dialog mit Millionen Followern —
                Creator-Commerce, wie er funktioniert.
              </p>
              <p>
                Der Sprung vom Feed in den stationären Handel: Mit der Mode-Kooperation bei Breuninger
                wurde Schlafi vom Social-Phänomen zur Marke im Premium-Retail — Creator-Reichweite
                trifft klassische Fashion-Welt.
              </p>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-[2rem]">
            <img loading="lazy" src="/schlafi/hotpants.jpg" alt="Schlafi Kollektion Kampagne (KI-generiert)" className="w-full" />
            <span className="ai-badge">KI-generiert</span>
          </div>
        </div>

        {/* product grid */}
        <h3 className="mono-label mt-16 text-xs text-[var(--crtr-yellow)]">DIE PRODUKTWELT))</h3>
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="schlafi-product-grid">
          {PRODUCTS.map((p) => (
            <div key={p.src} className="relative overflow-hidden rounded-2xl bg-neutral-900">
              <img loading="lazy" src={p.src} alt={`${p.alt} (KI-generiert)`} className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105" />
              <span className="ai-badge">KI-generiert</span>
            </div>
          ))}
        </div>
        <div className="relative mt-4 overflow-hidden rounded-[2rem]">
          <img loading="lazy" src="/schlafi/fruitdrop.jpg" alt="Schlafi Fruit Drop Kampagne (KI-generiert)" className="w-full object-cover" />
          <span className="ai-badge">KI-generiert</span>
        </div>

        {/* breuninger retail gallery */}
        <h3 className="mono-label mt-16 text-xs text-[var(--crtr-yellow)]">RETAIL @ BREUNINGER))</h3>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-white/80">
          Eigene Schlafi-Fläche im Breuninger: Vom Social-Drop auf die Premium-Verkaufsfläche —
          Creator-Commerce, der im stationären Handel ankommt.
        </p>
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3" data-testid="schlafi-breuninger-gallery">
          <div className="col-span-2 overflow-hidden rounded-2xl md:col-span-1">
            <img loading="lazy" src="/schlafi/breuninger-1.jpg" alt="Schlafi Fläche bei Breuninger" className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105" />
          </div>
          <div className="overflow-hidden rounded-2xl">
            <img loading="lazy" src="/schlafi/breuninger-2.jpg" alt="Schlafi Shorts bei Breuninger" className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105" />
          </div>
          <div className="overflow-hidden rounded-2xl">
            <img loading="lazy" src="/schlafi/breuninger-3.jpg" alt="Schlafi Display bei Breuninger" className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105" />
          </div>
        </div>

        {/* social proof */}
        <h3 className="mono-label mt-16 text-xs text-[var(--crtr-yellow)]">CREATOR CONTENT))</h3>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-white/80">
          Getragen von den Creator selbst: fionntime bespielt die Brand organisch in seinem Content
          und den Dart-Livestreams — Reichweite, die man nicht kaufen kann. Zusätzlich sorgt eine
          Mischung aus befreundeten Creator und gezieltem Sampling bei Macro-Influencern dafür,
          dass die Brand dauerhaft sichtbar bleibt und in den verschiedensten Umfeldern immer
          wieder auftaucht.
        </p>
        <div className="mt-6 flex flex-wrap gap-3" data-testid="schlafi-example-links">
          {[
            { label: "KLEINER BRUDER", href: "https://vm.tiktok.com/ZGdxKU1bX/" },
            { label: "MARIO", href: "https://vm.tiktok.com/ZGdxKAmyU/" },
            { label: "LEON", href: "https://vm.tiktok.com/ZGdxKFNwm/" },
          ].map((l) => (
            <a
              key={l.label}
              href={l.href}
              target="_blank"
              rel="noreferrer"
              className="mono-label inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs text-white/80 transition-colors hover:border-[var(--crtr-yellow)] hover:text-[var(--crtr-yellow)]"
            >
              <Music2 className="h-3.5 w-3.5" /> {l.label}
            </a>
          ))}
        </div>
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3" data-testid="schlafi-social-grid">
          <div className="overflow-hidden rounded-[1.6rem] ring-4 ring-black/80">
            <img loading="lazy" src="/schlafi/social-fionn.jpg" alt="Instagram-Post @fionntime" className="aspect-[9/16] w-full object-cover" />
          </div>
          <div className="overflow-hidden rounded-[1.6rem] ring-4 ring-black/80">
            <img loading="lazy" src="/schlafi/social-mario.jpg" alt="Instagram-Post @marionovembre" className="aspect-[9/16] w-full object-cover" />
          </div>
          <div className="col-span-2 overflow-hidden rounded-[1.6rem] ring-4 ring-black/80 md:col-span-1">
            <img loading="lazy" src="/creator-finn-pyjama.png" alt="Finn im Schlafi" className="aspect-[9/16] w-full object-cover object-top" />
          </div>
        </div>

        {/* CTAs */}
        <div className="mt-12 flex flex-wrap gap-4">
          <a
            href="https://schlafi-shop.de/"
            target="_blank"
            rel="noreferrer"
            data-testid="schlafi-shop-link"
            className="group inline-flex items-center gap-3 rounded-full bg-[var(--crtr-yellow)] py-3 pl-6 pr-3"
          >
            <span className="mono-label text-xs text-black">ZUM SCHLAFI SHOP</span>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black">
              <ExternalLink className="h-4 w-4 text-[var(--crtr-yellow)] transition-transform group-hover:translate-x-0.5" />
            </span>
          </a>
          <a
            href="https://www.tiktok.com/@schlafi_shop"
            target="_blank"
            rel="noreferrer"
            data-testid="schlafi-tiktok-link"
            className="group inline-flex items-center gap-3 rounded-full border border-[var(--crtr-yellow)] py-3 pl-6 pr-3"
          >
            <span className="mono-label text-xs text-[var(--crtr-yellow)]">TIKTOK @SCHLAFI_SHOP</span>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--crtr-yellow)]">
              <Music2 className="h-4 w-4 text-black" />
            </span>
          </a>
          <a
            href="https://www.instagram.com/schlafi_shop/"
            target="_blank"
            rel="noreferrer"
            data-testid="schlafi-instagram-link"
            className="group inline-flex items-center gap-3 rounded-full border border-[var(--crtr-yellow)] py-3 pl-6 pr-3"
          >
            <span className="mono-label text-xs text-[var(--crtr-yellow)]">@SCHLAFI_SHOP</span>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--crtr-yellow)]">
              <Instagram className="h-4 w-4 text-black" />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
